// AXEL Router admin panel — owner-only.
//
// Two cards live here:
//   1. Model Config — per-feature dropdown (Daily Brief / Research Desk / AXEL Tutor).
//      Saving POSTs /api/admin/axel-model-config and refreshes the registry.
//   2. Cost Tracker — table over /api/admin/axel-usage with day-window selector.
//
// Free-tier vs verified-tier behaviour is documented inline so the owner
// understands that flipping a model here only affects is_ib_verified users.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ax = { withCredentials: true };

const T = {
  bg: "#0a0e14", card: "#0d1218", border: "#1C1C1C",
  gold: "#C9A227", text: "#EDE8DC", mute: "#AAAAAA",
  green: "#22C55E", red: "#FF4444", yellow: "#FFB347", teal: "#14B8A6", purple: "#A78BFA",
};

// Average tokens per request — drives the "Est. cost / 1k requests" preview.
const TOKEN_ASSUMPTIONS = {
  daily_brief:   { in: 1200, out: 400, label: "Daily Brief" },
  research_desk: { in: 6000, out: 2500, label: "Research Desk" },
  axel_tutor:    { in: 3000, out: 1000, label: "AXEL Tutor" },
};

const PROVIDER_BADGE = {
  anthropic:  { bg: "#C9A22720", color: T.gold,   label: "Anthropic"  },
  deepseek:   { bg: "#22C55E20", color: T.green,  label: "DeepSeek"   },
  openrouter: { bg: "#A78BFA20", color: T.purple, label: "OpenRouter" },
};

const TIER_BADGE = {
  light:     { bg: "#14B8A622", color: T.teal,   label: "LIGHT"   },
  reasoning: { bg: "#A78BFA22", color: T.purple, label: "REASON"  },
  premium:   { bg: "#FFB34722", color: T.yellow, label: "PREMIUM" },
};

function estCostPer1k(model, feature) {
  if (!model || !feature) return null;
  const a = TOKEN_ASSUMPTIONS[feature];
  if (!a) return null;
  const cost = (a.in * 1000 / 1_000_000) * model.in_price + (a.out * 1000 / 1_000_000) * model.out_price;
  return cost;
}

export default function AxelRouterPanel() {
  const [config, setConfig] = useState(null);
  const [registry, setRegistry] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});
  const [saved, setSaved] = useState({});
  const [error, setError] = useState(null);

  const [usage, setUsage] = useState(null);
  const [usageDays, setUsageDays] = useState(7);
  const [usageLoading, setUsageLoading] = useState(false);

  const fetchConfig = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/admin/axel-model-config`, ax);
      setConfig(data.config || {});
      setRegistry(data.registry || []);
      setFeatures(data.features || []);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || "Failed to load config");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchUsage = useCallback(async (days) => {
    try {
      setUsageLoading(true);
      const { data } = await axios.get(`${API}/admin/axel-usage?days=${days}`, ax);
      setUsage(data);
    } catch (e) {
      console.warn("usage fetch failed:", e?.message || e);
    } finally {
      setUsageLoading(false);
    }
  }, []);

  useEffect(() => { fetchConfig(); }, [fetchConfig]);
  useEffect(() => { fetchUsage(usageDays); }, [fetchUsage, usageDays]);

  const saveFeature = async (feature, model) => {
    setSaving((s) => ({ ...s, [feature]: true }));
    setSaved((s) => ({ ...s, [feature]: null }));
    try {
      await axios.post(`${API}/admin/axel-model-config`, { feature, model }, ax);
      setConfig((c) => ({ ...c, [feature]: model }));
      setSaved((s) => ({ ...s, [feature]: "ok" }));
      setTimeout(() => setSaved((s) => ({ ...s, [feature]: null })), 2500);
    } catch (e) {
      setSaved((s) => ({ ...s, [feature]: "err" }));
      setError(e?.response?.data?.detail || e?.message || "Save failed");
    } finally {
      setSaving((s) => ({ ...s, [feature]: false }));
    }
  };

  // Group registry by provider for the dropdown
  const grouped = useMemo(() => {
    const g = {};
    registry.forEach((m) => { (g[m.provider] = g[m.provider] || []).push(m); });
    return g;
  }, [registry]);

  return (
    <div data-testid="axel-router-panel" style={{ padding: "0 24px 32px", display: "grid", gap: 18 }}>
      {/* ── Card 1: Model Config ─────────────────────────────────────── */}
      <div data-testid="axel-model-config-card" style={{
        background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.gold}`,
        borderRadius: 10, padding: 18,
      }}>
        <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4 }}>
          <span style={{ fontFamily: "monospace", color: T.gold, fontSize: 10, letterSpacing: 2 }}>
            ⚙️ AXEL MODEL CONFIG
          </span>
          <span style={{ color: T.mute, fontSize: 11 }}>· per-feature LLM router (verified-tier only)</span>
        </div>
        <h3 style={{ margin: "4px 0 14px", color: T.text, fontSize: 18, fontWeight: 800 }}>
          Choose model per AXEL feature
        </h3>

        {loading ? (
          <div style={{ color: T.mute, fontSize: 13, padding: "12px 0" }}>Loading config…</div>
        ) : error ? (
          <div style={{ color: T.red, fontSize: 13 }}>{error}</div>
        ) : (
          <div style={{ display: "grid", gap: 12 }}>
            {features.map((f) => {
              const currentKey = config?.[f];
              const currentModel = registry.find((m) => m.key === currentKey);
              const est = currentModel ? estCostPer1k(currentModel, f) : null;
              const tierBadge = currentModel ? TIER_BADGE[currentModel.tier] : null;
              return (
                <div key={f} data-testid={`axel-model-row-${f}`} style={{
                  background: "rgba(255,255,255,0.02)", border: `1px solid ${T.border}`,
                  borderRadius: 8, padding: 12,
                  display: "grid", gridTemplateColumns: "minmax(140px, 200px) 1fr auto", gap: 12, alignItems: "center",
                }}>
                  <div>
                    <div style={{ color: T.text, fontSize: 14, fontWeight: 800 }}>
                      {TOKEN_ASSUMPTIONS[f]?.label || f}
                    </div>
                    <div style={{ color: T.mute, fontSize: 10, fontFamily: "monospace", marginTop: 2 }}>
                      {f}
                    </div>
                  </div>

                  <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                    <select
                      data-testid={`axel-model-select-${f}`}
                      value={currentKey || ""}
                      disabled={saving[f]}
                      onChange={(e) => saveFeature(f, e.target.value)}
                      style={{
                        background: "#0a0e14", color: T.text,
                        border: `1px solid ${T.border}`, borderRadius: 6,
                        padding: "8px 10px", fontSize: 13, minWidth: 230,
                        fontFamily: "monospace",
                      }}>
                      {Object.entries(grouped).map(([prov, models]) => (
                        <optgroup key={prov} label={PROVIDER_BADGE[prov]?.label || prov}>
                          {models.map((m) => (
                            <option key={m.key} value={m.key} disabled={!m.available}>
                              {m.label} {!m.available ? "— key missing" : ""}
                              {f === "axel_tutor" && !m.multi_turn && m.available ? " (stateless)" : ""}
                            </option>
                          ))}
                        </optgroup>
                      ))}
                    </select>
                    {currentModel && tierBadge && (
                      <span style={{
                        background: tierBadge.bg, color: tierBadge.color,
                        padding: "3px 8px", borderRadius: 4,
                        fontFamily: "monospace", fontSize: 9, letterSpacing: 1, fontWeight: 800,
                      }}>{tierBadge.label}</span>
                    )}
                    {est !== null && (
                      <span data-testid={`axel-model-est-${f}`} style={{
                        color: T.mute, fontSize: 11, fontFamily: "monospace",
                      }}>
                        ≈ ${est.toFixed(2)}/1k req
                      </span>
                    )}
                  </div>

                  <div style={{ minWidth: 70, textAlign: "right" }}>
                    {saving[f] ? (
                      <span style={{ color: T.yellow, fontSize: 11, fontFamily: "monospace" }}>Saving…</span>
                    ) : saved[f] === "ok" ? (
                      <span style={{ color: T.green, fontSize: 11, fontFamily: "monospace" }}>✓ Active</span>
                    ) : saved[f] === "err" ? (
                      <span style={{ color: T.red, fontSize: 11, fontFamily: "monospace" }}>Error</span>
                    ) : (
                      <span style={{ color: T.green, fontSize: 11, fontFamily: "monospace" }}>● Active</span>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <div style={{
          marginTop: 14, padding: "10px 12px", borderRadius: 6,
          background: "rgba(201,162,39,0.06)", border: `1px solid ${T.gold}30`,
          color: T.gold, fontSize: 11, lineHeight: 1.55, fontFamily: "monospace",
        }}>
          ⚠ Free users (is_ib_verified=false) are <b>always</b> routed to cheap-tier models
          with tight token caps regardless of this config. Verified users get the model
          you choose above. Tutor only supports multi-turn on Anthropic models —
          DeepSeek/OpenRouter Tutor sessions are stateless.
        </div>
      </div>

      {/* ── Card 2: Cost Tracker ─────────────────────────────────────── */}
      <div data-testid="axel-cost-tracker-card" style={{
        background: T.card, border: `1px solid ${T.border}`, borderLeft: `3px solid ${T.teal}`,
        borderRadius: 10, padding: 18,
      }}>
        <div style={{
          display: "flex", alignItems: "flex-end", justifyContent: "space-between",
          gap: 12, flexWrap: "wrap", marginBottom: 14,
        }}>
          <div>
            <div style={{ fontFamily: "monospace", color: T.teal, fontSize: 10, letterSpacing: 2 }}>
              📊 AXEL COST TRACKER
            </div>
            <h3 style={{ margin: "4px 0 0", color: T.text, fontSize: 18, fontWeight: 800 }}>
              Per-feature per-model usage
            </h3>
          </div>
          <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <select
              data-testid="axel-usage-days-select"
              value={usageDays}
              onChange={(e) => setUsageDays(parseInt(e.target.value, 10))}
              style={{
                background: "#0a0e14", color: T.text,
                border: `1px solid ${T.border}`, borderRadius: 6,
                padding: "6px 10px", fontSize: 12, fontFamily: "monospace",
              }}>
              <option value={1}>Last 24h</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
            </select>
            <button
              data-testid="axel-usage-refresh-btn"
              onClick={() => fetchUsage(usageDays)}
              disabled={usageLoading}
              style={{
                all: "unset", cursor: usageLoading ? "wait" : "pointer",
                padding: "6px 12px", borderRadius: 6,
                border: `1px solid ${T.gold}55`, color: T.gold,
                fontFamily: "monospace", fontSize: 10, letterSpacing: 1.5, fontWeight: 700,
                background: `${T.gold}10`,
              }}>{usageLoading ? "…" : "↻ REFRESH"}</button>
          </div>
        </div>

        {!usage ? (
          <div style={{ color: T.mute, fontSize: 13, padding: "10px 0" }}>Loading usage…</div>
        ) : usage.rows.length === 0 ? (
          <div data-testid="axel-usage-empty" style={{
            color: T.mute, fontSize: 13, padding: "16px 12px", textAlign: "center",
            border: `1px dashed ${T.border}`, borderRadius: 8,
          }}>
            No router activity in the last {usageDays} day{usageDays > 1 ? "s" : ""} yet.
          </div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table data-testid="axel-usage-table" style={{
              width: "100%", borderCollapse: "collapse", fontSize: 12, fontFamily: "monospace",
              minWidth: 700,
            }}>
              <thead>
                <tr style={{ color: T.mute, textAlign: "left" }}>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}` }}>FEATURE</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}` }}>MODEL</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textAlign: "right" }}>REQUESTS</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textAlign: "right" }}>INPUT TOK</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textAlign: "right" }}>OUTPUT TOK</th>
                  <th style={{ padding: "8px 10px", borderBottom: `1px solid ${T.border}`, textAlign: "right" }}>COST USD</th>
                </tr>
              </thead>
              <tbody>
                {usage.rows.map((r) => {
                  const prov = PROVIDER_BADGE[r.provider] || {};
                  return (
                    <tr key={`${r.feature}-${r.model}`} data-testid={`axel-usage-row-${r.feature}-${r.model}`}>
                      <td style={{ padding: "8px 10px", color: T.text, borderBottom: `1px solid ${T.border}` }}>
                        {TOKEN_ASSUMPTIONS[r.feature]?.label || r.feature}
                      </td>
                      <td style={{ padding: "8px 10px", color: T.text, borderBottom: `1px solid ${T.border}` }}>
                        <span style={{ marginRight: 6 }}>{r.model}</span>
                        {prov.label && (
                          <span style={{
                            background: prov.bg, color: prov.color,
                            padding: "2px 6px", borderRadius: 3, fontSize: 9, fontWeight: 800, letterSpacing: 1,
                          }}>{prov.label}</span>
                        )}
                        {r.errors > 0 && (
                          <span style={{
                            marginLeft: 6, color: T.red, fontSize: 9, fontWeight: 800,
                            padding: "2px 6px", borderRadius: 3, background: "#FF444418", border: `1px solid #FF444440`,
                          }}>{r.errors} ERR</span>
                        )}
                      </td>
                      <td style={{ padding: "8px 10px", color: T.text, textAlign: "right", borderBottom: `1px solid ${T.border}` }}>
                        {r.requests.toLocaleString()}
                      </td>
                      <td style={{ padding: "8px 10px", color: T.mute, textAlign: "right", borderBottom: `1px solid ${T.border}` }}>
                        {r.input_tokens.toLocaleString()}
                      </td>
                      <td style={{ padding: "8px 10px", color: T.mute, textAlign: "right", borderBottom: `1px solid ${T.border}` }}>
                        {r.output_tokens.toLocaleString()}
                      </td>
                      <td style={{ padding: "8px 10px", color: T.gold, textAlign: "right", fontWeight: 800, borderBottom: `1px solid ${T.border}` }}>
                        ${r.cost_usd.toFixed(4)}
                      </td>
                    </tr>
                  );
                })}
                <tr data-testid="axel-usage-total-row">
                  <td colSpan={2} style={{ padding: "10px", color: T.gold, fontWeight: 800 }}>TOTAL</td>
                  <td style={{ padding: "10px", color: T.text, textAlign: "right", fontWeight: 800 }}>
                    {usage.total_requests?.toLocaleString?.() ?? 0}
                  </td>
                  <td colSpan={2}></td>
                  <td data-testid="axel-usage-total-cost" style={{
                    padding: "10px", color: T.green, textAlign: "right", fontWeight: 800, fontSize: 14,
                  }}>
                    ${(usage.total_cost_usd || 0).toFixed(4)}
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
